import { DIRECTIONS, type Direction } from "src/shared/board";
import { dialOffset } from "./facingAngles";
import { motion, styled } from "./kit";

/**
 * Which way a new pawn should face, asked the way the board looks.
 *
 * It replaced four buttons reading `N E S W`, which asked the player to hold
 * the board's compass in their head: the board turns with the mouse and snaps
 * every 90°, so "north" is up, or right, or down, and the control said the
 * same thing whichever it was. Here each arrow sits where that direction
 * actually goes and points that way, so choosing one is pointing at the board
 * rather than translating for it.
 *
 * **The angles are measured, not derived** — see `facingAngles`. With none
 * (no renderer yet) it falls back to the compass points, which is the old
 * control's behaviour rather than a new kind of wrong.
 *
 * Built on the kit (PLAN-UI §28.2): every colour is a `theme.kit` token or a
 * `color-mix` over one, so the dial moves with the sheet. Geometry did not
 * move — the ring, the radius and the offsets are the numbers that were
 * measured against the projection.
 */

const RADIUS = 26;
const FALLBACK: Record<Direction, number> = (() => {
  const quarter = Math.PI / 2;
  const out = {} as Record<Direction, number>;
  DIRECTIONS.forEach((direction, index) => {
    out[direction] = index * quarter;
  });
  return out;
})();

/**
 * The ring is the board's horizon: it says the thing inside is about where you
 * are looking, not about a compass.
 */
const Dial = styled("div")(({ theme }) => ({
  position: "relative",
  width: RADIUS * 2 + 30,
  height: RADIUS * 2 + 30,
  marginTop: theme.kit.space.xs,
  borderRadius: theme.kit.radius.pill,
  border: `1px dashed ${theme.kit.color.line}`
}));

export function FacingDial({
  value,
  angles,
  onPick,
  disabled
}: {
  /** `null` is "you decide" — the foundry's default. */
  value: Direction | null;
  angles: Record<Direction, number> | null;
  onPick: (facing: Direction | null) => void;
  disabled?: boolean;
}) {
  const dial = angles ?? FALLBACK;

  return (
    <Dial>
      {/* "You decide" lives in the middle, because it is the absence of a
          direction rather than a fifth one. */}
      <Pip
        testid="mint-facing-auto"
        label="auto"
        glyph="◦"
        on={value === null}
        disabled={disabled}
        onClick={() => onPick(null)}
        x={0}
        y={0}
      />
      {DIRECTIONS.map((direction) => {
        const at = dialOffset(dial[direction], RADIUS);
        return (
          <Pip
            key={direction}
            testid={`mint-facing-${direction}`}
            label={`face ${LABEL[direction]}`}
            glyph="▲"
            // The glyph is drawn pointing up and turned to point along the
            // direction, so it agrees with where it is sitting by
            // construction — two independent placements would drift.
            rotate={dial[direction]}
            on={value === direction}
            disabled={disabled}
            onClick={() => onPick(direction)}
            x={at.x}
            y={at.y}
          />
        );
      })}
    </Dial>
  );
}

/** Only for the accessible name — the control itself never says "north". */
const LABEL: Record<Direction, string> = (() => {
  const names = ["north", "east", "south", "west"];
  const out = {} as Record<Direction, string>;
  DIRECTIONS.forEach((direction, index) => {
    out[direction] = names[index] ?? String(direction);
  });
  return out;
})();

/**
 * One arrow, or the centre "you decide".
 *
 * **Off is `aria-disabled`, never the native attribute** — the rule `Tile`
 * follows, for its reason: a native `disabled` suppresses the element's
 * `title`, and an unexplained dead control is the thing this file's refusals
 * exist to avoid. The click goes; the pointer does not.
 *
 * **Nothing animates its transform.** Colour transitions, position does not:
 * the dial is re-measured when the board has turned a degree or more, and an
 * eased transform would trail the board it is describing.
 */
const PipRoot = styled("button", {
  shouldForwardProp: (prop: string) => prop !== "on"
})<{ on?: boolean }>(({ theme, on }) => ({
  position: "absolute",
  left: "50%",
  top: "50%",
  width: 22,
  height: 22,
  padding: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: theme.kit.radius.pill,
  fontFamily: theme.kit.font.body,
  fontSize: 11,
  lineHeight: 1,
  cursor: "pointer",
  // The lit pip is the one brand thing in the dial, and its wash is derived
  // from the same token rather than minted beside it — `Tile`'s armed rule.
  border: `1px solid ${on ? theme.kit.color.brand : theme.kit.color.line}`,
  background: on
    ? `color-mix(in srgb, ${theme.kit.color.brand} 14%, ${theme.kit.color.ground})`
    : `color-mix(in srgb, ${theme.kit.color.panelRaised} 85%, transparent)`,
  color: on ? theme.kit.color.brand : theme.kit.color.ink,
  ...motion([], ["background", "border-color", "color"]),

  "&:hover": { borderColor: on ? theme.kit.color.brand : theme.kit.color.lineHi },
  "&:focus-visible": {
    outline: `2px solid ${theme.kit.color.brand}`,
    outlineOffset: 2
  },
  // Same specificity as `&:hover` and after it, so it wins.
  '&[aria-disabled="true"]': {
    opacity: 0.4,
    cursor: "default",
    color: theme.kit.color.inkDim,
    borderColor: theme.kit.color.line
  }
}));

/** The arrow itself, turned to point along its direction. */
const Glyph = styled("span")({ display: "block" });

function Pip({
  testid,
  label,
  glyph,
  rotate = 0,
  on,
  disabled,
  onClick,
  x,
  y
}: {
  testid: string;
  label: string;
  glyph: string;
  rotate?: number;
  on: boolean;
  disabled?: boolean;
  onClick: () => void;
  x: number;
  y: number;
}) {
  return (
    <PipRoot
      data-testid={testid}
      aria-label={label}
      aria-pressed={on}
      aria-disabled={disabled || undefined}
      on={on}
      onClick={disabled ? undefined : onClick}
      // `style`, not `sx`: these carry per-instance numbers, and emotion mints
      // a generated class per distinct value — the trap that grew the document
      // by hundreds of permanent rules a move.
      style={{ transform: `translate(-50%, -50%) translate(${x}px, ${y}px)` }}
    >
      <Glyph style={{ transform: `rotate(${rotate}rad)` }}>{glyph}</Glyph>
    </PipRoot>
  );
}
